/* useVoiceCall — unified React hook for 1-to-1 voice calls.

   ARCHITECTURE
   ============
   We support two engines behind the same public API:

     • NATIVE iOS / Android  →  `lexAgora` plugin (Swift / Java) which
       drives the official Agora native SDK in-process.  This is the
       ONLY path that can route call audio to the EARPIECE on iOS
       WKWebView / Android WebView (WhatsApp-style behavior).

     • WEB / PWA            →  `agora-rtc-sdk-ng` (the JS SDK) loaded
       lazily so the ~7 MB chunk stays out of the initial bundle.

   The hook auto-selects the right engine at runtime via
   `hasNativeAgora` and exposes a single contract:

     join({ appId, channel, token, uid }) / leave()
     toggleMute() / toggleSpeaker()
     joined, remoteUsers, isMuted, isSpeakerOn, error

   Callers never need to know which engine is in use.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import {
  hasNativeAgora,
  initialize as nativeInit,
  joinChannel as nativeJoin,
  leaveChannel as nativeLeave,
  setMute as nativeSetMute,
  setSpeaker as nativeSetSpeaker,
  addListener as nativeAddListener,
} from "../lib/lexAgora";

/* ------------------------------------------------------------------ */
/* Web-only: lazy-load agora-rtc-sdk-ng                               */
/* ------------------------------------------------------------------ */
let _AgoraPromise = null;
async function loadAgora() {
  if (!_AgoraPromise) {
    _AgoraPromise = import("agora-rtc-sdk-ng").then((mod) => {
      const A = mod.default || mod;
      try { A.setLogLevel(3); } catch { /* noop */ }
      return A;
    });
  }
  return _AgoraPromise;
}
export function prewarmAgora() {
  if (hasNativeAgora) return; // not needed when using native SDK
  loadAgora().catch(() => { /* noop */ });
}

/* ------------------------------------------------------------------ */
/* Web-only: <audio playsinline> patch so PWA on iOS Safari can       */
/* route to the earpiece via navigator.audioSession.                  */
/* ------------------------------------------------------------------ */
try {
  if (!hasNativeAgora && typeof document !== "undefined" && !document.__lexAudioCreatePatched) {
    const origCreate = document.createElement.bind(document);
    document.createElement = function (tag, options) {
      const el = options !== undefined ? origCreate(tag, options) : origCreate(tag);
      try {
        if (tag && String(tag).toLowerCase() === "audio") {
          el.setAttribute("playsinline", "");
          el.setAttribute("webkit-playsinline", "");
          try { el.playsInline = true; } catch { /* noop */ }
        }
      } catch { /* noop */ }
      return el;
    };
    document.__lexAudioCreatePatched = true;
  }
} catch { /* noop */ }

/* ================================================================== */
export function useVoiceCall() {
  const [joined, setJoined] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [error, setError] = useState(null);

  /* ============= NATIVE PATH (iOS + Android) ===================== */
  const nativeListenersRef = useRef([]);

  useEffect(() => {
    if (!hasNativeAgora) return;
    // Wire up Agora native events → React state.  Each listener is
    // tracked so we can clean them up on unmount.
    const unsubs = [];
    unsubs.push(nativeAddListener("userJoined", ({ uid }) => {
      setRemoteUsers((prev) => (prev.find((u) => u.uid === uid) ? prev : [...prev, { uid }]));
    }));
    unsubs.push(nativeAddListener("userOffline", ({ uid }) => {
      setRemoteUsers((prev) => prev.filter((u) => u.uid !== uid));
      try { window.dispatchEvent(new CustomEvent("lex:remote-peer-left")); } catch { /* noop */ }
    }));
    unsubs.push(nativeAddListener("joinSuccess", () => { setJoined(true); }));
    unsubs.push(nativeAddListener("connectionLost", () => {
      setError("Connection lost");
    }));
    unsubs.push(nativeAddListener("error", ({ message }) => { setError(message); }));
    nativeListenersRef.current = unsubs;
    return () => { unsubs.forEach((u) => { try { u(); } catch { /* noop */ } }); };
  }, []);

  /* ============= WEB PATH (PWA / desktop) ======================== */
  const clientRef = useRef(null);
  const localTrackRef = useRef(null);

  const webEnsureClient = useCallback(async () => {
    if (clientRef.current) return clientRef.current;
    const AgoraRTC = await loadAgora();
    const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
    client.on("user-published", async (user, mediaType) => {
      try {
        await client.subscribe(user, mediaType);
        if (mediaType === "audio") {
          try { user.audioTrack?.play(); } catch { /* noop */ }
        }
      } catch { /* noop */ }
      setRemoteUsers([...client.remoteUsers]);
    });
    client.on("user-left", () => {
      setRemoteUsers([...client.remoteUsers]);
      try { window.dispatchEvent(new CustomEvent("lex:remote-peer-left")); } catch { /* noop */ }
    });
    clientRef.current = client;
    return client;
  }, []);

  /* ============= JOIN ============================================ */
  const join = useCallback(async ({ appId, channel, token, uid }) => {
    setError(null);
    if (hasNativeAgora) {
      const initRes = await nativeInit({ appId });
      if (!initRes.ok) { setError(initRes.error || "Agora init failed"); throw new Error(initRes.error || "init failed"); }
      const joinRes = await nativeJoin({ token, channel, uid: Number(uid) });
      if (!joinRes.ok) { setError(joinRes.error || "Join failed"); throw new Error(joinRes.error || "join failed"); }
      setIsSpeakerOn(false);
      return;
    }
    // ----- Web path -----
    try {
      const AgoraRTC = await loadAgora();
      const client = await webEnsureClient();
      // iOS PWA earpiece preference (best-effort).
      try {
        if (typeof navigator !== "undefined" && navigator.audioSession) {
          navigator.audioSession.type = "play-and-record";
        }
      } catch { /* noop */ }
      setIsSpeakerOn(false);
      const [, mic] = await Promise.all([
        client.join(appId, channel, token, Number(uid)),
        AgoraRTC.createMicrophoneAudioTrack({
          encoderConfig: "speech_standard",
          AEC: true,
          ANS: true,
          AGC: true,
        }),
      ]);
      localTrackRef.current = mic;
      await client.publish(mic);
      setJoined(true);
      setRemoteUsers([...client.remoteUsers]);
    } catch (e) {
      setError(e?.message || String(e));
      throw e;
    }
  }, [webEnsureClient]);

  /* ============= LEAVE =========================================== */
  const leave = useCallback(async () => {
    if (hasNativeAgora) {
      try { await nativeLeave(); } catch { /* noop */ }
      setJoined(false);
      setIsMuted(false);
      setIsSpeakerOn(false);
      setRemoteUsers([]);
      return;
    }
    // ----- Web path -----
    const client = clientRef.current;
    const mic = localTrackRef.current;
    try {
      if (mic) { mic.stop(); mic.close(); }
      if (client) {
        try { await client.unpublish(); } catch { /* noop */ }
        try { await client.leave(); } catch { /* noop */ }
      }
    } finally {
      localTrackRef.current = null;
      setJoined(false);
      setIsMuted(false);
      setIsSpeakerOn(false);
      setRemoteUsers([]);
      try {
        if (typeof navigator !== "undefined" && navigator.audioSession) {
          navigator.audioSession.type = "auto";
        }
      } catch { /* noop */ }
    }
  }, []);

  /* ============= MUTE ============================================ */
  const toggleMute = useCallback(async () => {
    const next = !isMuted;
    if (hasNativeAgora) {
      await nativeSetMute(next);
      setIsMuted(next);
      return;
    }
    const mic = localTrackRef.current;
    if (!mic) return;
    await mic.setEnabled(!next);
    setIsMuted(next);
  }, [isMuted]);

  /* ============= SPEAKER ========================================= */
  const toggleSpeaker = useCallback(async () => {
    const next = !isSpeakerOn;
    if (hasNativeAgora) {
      const res = await nativeSetSpeaker(next);
      // Trust the native plugin's reported state — it knows whether
      // iOS actually honored the request.
      setIsSpeakerOn(res?.speakerOn ?? next);
      return;
    }
    // Web fallback: WebKit's experimental audioSession API.
    try {
      if (typeof navigator !== "undefined" && navigator.audioSession) {
        navigator.audioSession.type = next ? "playback" : "play-and-record";
      }
    } catch { /* noop */ }
    setIsSpeakerOn(next);
  }, [isSpeakerOn]);

  /* ============= UNMOUNT CLEANUP ================================= */
  useEffect(() => {
    return () => {
      if (hasNativeAgora) {
        try { nativeLeave(); } catch { /* noop */ }
        return;
      }
      const client = clientRef.current;
      const mic = localTrackRef.current;
      if (mic) { try { mic.stop(); mic.close(); } catch { /* noop */ } }
      if (client) {
        try { client.leave(); } catch { /* noop */ }
        try { client.removeAllListeners(); } catch { /* noop */ }
      }
      clientRef.current = null;
      localTrackRef.current = null;
    };
  }, []);

  return { join, leave, toggleMute, toggleSpeaker, joined, remoteUsers, isMuted, isSpeakerOn, error };
}
